import { Dialog } from "components/ui";
import useWindowSize from "components/ui/hooks/useWindowSize";
import React, { useEffect, useMemo, useState } from "react";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { setOnboard } from "store/base/commonSlice";
import useAuthority from "utils/hooks/useAuthority";
import getOnboardViewport from "utils/getOnboardViewport";
import { setItem } from "utils/safeStorage";

const CUSTOMER_MOBILE_SLIDE_COUNT = {
  en: 11,
  tr: 9,
};

const CUSTOMER_WEB_SLIDE_COUNT = 8;

const buildOnboardImages = (filePrefix, count, locale, isMobile) =>
  Array.from(
    { length: count },
    (_, index) =>
      `/img/onboard/${filePrefix}-${index + 1}-${locale}-${
        isMobile ? "mobil" : "web"
      }.png`,
  );

const PlatformOnboard = () => {
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const locale = useSelector((state) => state.theme.locale);
  const userAuthority = useSelector((state) => state.auth.user?.authority);
  const showToBusiness = useAuthority(userAuthority, [
    "Admin",
    "Operator",
    "Manager",
  ]);
  const showToRecycler = useAuthority(userAuthority, [
    "Recycler",
    "Refurbisher",
  ]);

  const [isOpen, setIsOpen] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  const resolvedLocale = locale === "en" ? "en" : "tr";
  const isMobile = getOnboardViewport(width);

  const businessImages = useMemo(
    () => buildOnboardImages("onboard", 13, resolvedLocale, isMobile),
    [resolvedLocale, isMobile],
  );

  const customerImages = useMemo(
    () =>
      isMobile
        ? buildOnboardImages(
            "customer-onboard",
            CUSTOMER_MOBILE_SLIDE_COUNT[resolvedLocale] ??
              CUSTOMER_WEB_SLIDE_COUNT,
            resolvedLocale,
            true,
          )
        : buildOnboardImages(
            "customer-onboard",
            CUSTOMER_WEB_SLIDE_COUNT,
            resolvedLocale,
            false,
          ),
    [resolvedLocale, isMobile],
  );

  const recyclerImages = useMemo(
    () => [
      `/img/onboard/recycler-onboard-1-${resolvedLocale}-${
        isMobile ? "mobil" : "web"
      }.png`,
      `/img/onboard/recycler-onboard-2-${resolvedLocale}-${
        isMobile ? "mobil" : "web"
      }.png`,
      `/img/onboard/recycler-onboard-3-${resolvedLocale}-${
        isMobile ? "mobil" : "web"
      }.png`,
      `/img/onboard/recycler-onboard-4-${resolvedLocale}-${
        isMobile ? "mobil" : "web"
      }.png`,
      `/img/onboard/recycler-onboard-5-${resolvedLocale}-${
        isMobile ? "mobil" : "web"
      }.png`,
      `/img/onboard/recycler-onboard-6-${resolvedLocale}-${
        isMobile ? "mobil" : "web"
      }.png`,
      ...(isMobile
        ? [
            `/img/onboard/recycler-onboard-7-${resolvedLocale}-mobil.png`,
            `/img/onboard/recycler-onboard-8-${resolvedLocale}-mobil.png`,
            `/img/onboard/recycler-onboard-9-${resolvedLocale}-mobil.png`,
          ]
        : []),
    ],
    [resolvedLocale, isMobile],
  );

  const displayImages = showToRecycler
    ? recyclerImages
    : showToBusiness
      ? businessImages
      : customerImages;

  useEffect(() => {
    setSelectedImage((current) =>
      current >= displayImages.length ? 0 : current,
    );
  }, [displayImages.length]);

  const handleArrowClick = (type) => {
    if (type === "next") {
      setSelectedImage((current) =>
        current < displayImages.length - 1 ? current + 1 : 0,
      );
      return;
    }

    setSelectedImage((current) =>
      current > 0 ? current - 1 : displayImages.length - 1,
    );
  };

  const handleStart = () => {
    dispatch(setOnboard(true));
    setItem("onboard", "true");
    setIsOpen(false);
  };

  const handleClose = () => {
    dispatch(setOnboard(true));
    setItem("onboard", "true");
    setIsOpen(false);
  };

  const currentImage = displayImages[selectedImage];

  return (
    <Dialog
      closable={true}
      isOpen={isOpen}
      overlayClassName="flex items-center justify-center p-2 h-screen z-[40]"
      className="flex-1 flex items-center justify-center p-1 rounded-md shadow-lg max-w-[min(100vw-1rem,960px)]"
      contentClassName="p-1 !h-auto"
      onClose={() => handleClose()}
      onRequestClose={() => handleClose()}
      closeButtonClass="md:!right-7 md:!top-7 !right-2.5 !top-2.5"
    >
      <div className="relative flex items-center justify-center">
        {currentImage ? (
          <img
            alt="welcome"
            src={currentImage}
            className="select-none block max-w-[min(100vw-2rem,920px)] max-h-[85vh] w-auto h-auto object-contain"
          />
        ) : null}
        {selectedImage === displayImages.length - 1 ? (
          <div
            className="absolute top-1/2 -right-0 p-2 bg-slate-50 text-kostebek-500 cursor-pointer hover:bg-kostebek-500 hover:text-white"
            onClick={() => handleStart()}
          >
            <p className="text-lg font-semibold">
              {resolvedLocale === "tr" ? "Başla" : "Start"}
            </p>
          </div>
        ) : (
          <div
            className="absolute top-1/2 -right-0 p-2 bg-slate-50 text-kostebek-500 cursor-pointer hover:bg-kostebek-500 hover:text-white"
            onClick={() => handleArrowClick("next")}
          >
            <HiChevronRight className="text-xl md:text-3xl" />
          </div>
        )}
        {selectedImage === 0 ? (
          <span></span>
        ) : (
          <div
            className="absolute top-1/2 -left-0 p-2 bg-slate-50 text-kostebek-500 cursor-pointer hover:bg-kostebek-500 hover:text-white"
            onClick={() => handleArrowClick("prev")}
          >
            <HiChevronLeft className="text-xl md:text-3xl" />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default PlatformOnboard;
